import { trackEvent } from '../../../utils/analytics';
import { templatePath } from '../../../utils/appClickTracking';

/**
 * Chat Files analytics (CEL-3473 round 5, Nitish Oct 15 2026: "I should know how many users have opened this panel
 * and how many users have clicked on and interacted with those CTAs"). Event list: the build summary's
 * "Analytics events".
 *
 * Every gallery action is ONE `app_click` with element `chatfiles_*` and surface `chat_files`, the same shape the
 * delegated click layer (appClickTracking.js) emits, so one query reads clicks, keys and swipes alike. This helper is
 * for what the layer can't see (keys, swipes, a tap that opens a file through the shared tiles, a download inside
 * them, state shown); buttons of our own keep `data-track`. `detail` is a controlled word, never a file name or id.
 */
export const CHAT_FILES_SURFACE = 'chat_files';

// chat_type (round 6, Nitish Oct 16 2026): every chatfiles event and the app_view say which kind of chat the list
// belongs to, 'employee' or 'chat' (a controlled word, never an id). The hosts that know the chat set it
// (useChatFilesTarget); the shared full-screen viewer logs through trackChatFiles, so it carries it too. The click
// layer can't add a property, so every chatfiles button logs through here (no data-track on them any more).
let chatType = 'chat';
export const setChatFilesChatType = (type) => {
  chatType = type === 'employee' ? 'employee' : 'chat';
};

// `from` (round 6h, review item 5): the element clicked, so `container` is read exactly as the click layer read it (the
// nearest [data-track-container] ancestor: BottomSheet's "sheet"); the buttons that moved off the layer keep it.
// `origin` (one viewer, Oct 16 2026): where the viewer's file was opened, 'files' (the gallery) or 'chat' (a click on a
// file in a chat message, the same viewer over the same list). Every viewer event carries it, so the Files numbers are
// `origin = 'files'`; events from before this field are all from the gallery. A controlled word; anything else is dropped.
export const VIEWER_ORIGINS = ['files', 'chat'];
export function trackChatFiles(element, detail, surface = CHAT_FILES_SURFACE, { from = null, origin = null } = {}) {
  try {
    const props = { element, surface, page_path: templatePath(window.location.pathname) };
    if (detail) props.detail = detail;
    props.chat_type = chatType;
    if (VIEWER_ORIGINS.includes(origin)) props.origin = origin;
    const host = from && from.closest ? from.closest('[data-track-container]') : null;
    if (host) props.container = host.getAttribute('data-track-container');
    trackEvent('app_click', props);
  } catch (err) { /* analytics must never break product flows */ }
}

/** The panel / sheet mounted open: the denominator for every chatfiles_* event (detail = panel | sheet). */
export function trackChatFilesView(surface) {
  try {
    trackEvent('app_view', {
      surface: CHAT_FILES_SURFACE,
      detail: surface,
      page_path: templatePath(window.location.pathname),
      chat_type: chatType,
    });
  } catch (err) { /* analytics must never break product flows */ }
}

// The viewer's origin for its events: 'files' / 'chat' while it shows a file of the Files list, else null (a viewer that
// logs nothing: channels, the Agent Computer, a chat file that fell back to the chat's own set).
export const viewerOrigin = ({ fromGallery, fromChat } = {}) => {
  if (fromGallery) return 'files';
  return fromChat ? 'chat' : null;
};

// The cell's kind as a controlled word (image | video | audio | file); never the file's name or type string.
export const tileKind = (file, image) => {
  if (image) return 'image';
  if ((file.mime || '').toLowerCase().startsWith('video/')) return 'video';
  return file.kind === 'audio' ? 'audio' : 'file';
};
